import {cloneDeep, orderBy} from "lodash";
import {call, fork, put, race, select, take, takeLatest} from "redux-saga/effects";
import {
  acknowledgePowerOnOff,
  createProbesAsync,
  deleteSensorDetailsAsync,
  editController,
  getAvailablePinsAsync,
  getControllers,
  getIOTypesAsync,
  getProbesAsync,
  getSensorCalibrationCommandsAsync,
  getSensorCalibrationHistoryAsync,
  getSensorDetailsAsync,
  getSensorListAsync,
  getSummaryDataAsync,
  getSummarySensorDataAsync,
  getSystemInventories,
  getTelemetryAsync,
  requestAddSlugFeedAsync,
  requestAnalogBankAsync,
  requestFavouriteAsync,
  requestFavouritesAsync,
  requestSensorSummaryAsync,
  requestSnapshotAsync,
  requestSystemLogsAsync,
  requestTestFeedAsync,
  runSensorCalibrationCommandsAsync,
  sendAnalogBankAsync,
  sendFavouriteAsync,
  updateSensorDetailsAsync,
  resetControllerAsync,
  resetLimitSensorAsync,
  getCategories,
  getTypesAsync,
  getGroupsAsync,
  getGroupsByCategoryAsync,
  getSelectedGroupTypeAsync,
  updateSensorGroupTypeAsync,
  getControllersWithActiveAlertAsync,
  getControllerSensorDataAsync,
} from "../AsyncActions/controllers.async";
import * as actions from "../action.constants";
import {storeSnapshot} from "../Actions/controllers.actions";
import {getCurrentCustomer} from "../Selectors/customers.selectors";
import {getCurrentSite, getSelectedLocations} from "../Selectors/sites.selectors";
import {getSelectedController} from "../Selectors/controllers.selectors";

export function* controllersRequest(action = {}) {
  const {pageUrl = "", trackPromise = true} = action;
  try {
    const currentCustomer = yield select(getCurrentCustomer);
    const currentSite = yield select(getCurrentSite);
    const currentLocation = yield select(getSelectedLocations);

    if (currentSite?.id && currentLocation?.id) {
      const response = yield call(
        getControllers,
        {
          customerId: currentCustomer?.id,
          siteId: currentSite?.id,
          locationId: currentLocation?.id,
        },
        pageUrl,
        trackPromise
      );
      if (response) {
        yield put({
          type: actions.STORE_CONTROLLERS,
          payload: response?.data?.data,
        });
      }
    }
  } catch (e) {
    /* empty */
  }
}

function* telemetryRequest(action = {}) {
  const {callback = () => {}} = action;
  try {
    const selectedController = yield select(getSelectedController);
    const response = yield call(getTelemetryAsync, {mac_id: selectedController.mac});
    if (response?.data?.data) {
      callback(response?.data?.data);
    }
  } catch (_e) {
    //
  }
}

function* systemPropertiesRequest(action = {}) {
  const {trackPromise = false} = action;
  try {
    const selectedController = yield select(getSelectedController);
    if (selectedController?.mac) {
      const response = yield call(getSystemInventories, trackPromise, {
        mac_id: selectedController?.mac,
      });
      if (response?.data) {
        yield put({
          type: actions.STORE_SYSTEM_PROPERTIES,
          payload: response?.data,
        });
      }
    }
  } catch (e) {
    /* empty */
  }
}

function* ioTypesRequest(action = {}) {
  const {callback = () => {}} = action;
  const selectedController = yield select(getSelectedController);

  try {
    const response = yield call(getIOTypesAsync, {mac_id: selectedController?.mac});
    if (response?.data) {
      if (callback) {
        callback(response?.data?.data);
      }
    }
  } catch (e) {
    /* empty */
  }
}

function* probesRequest(action = {}) {
  const {payload, callback = () => {}} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(getProbesAsync, {
      mac_id: selectedController?.mac,
      ioType: payload,
    });
    if (response?.data) {
      callback(response?.data?.data);
    } else {
      callback();
    }
  } catch (_e) {
    callback();
  }
}

function* sensorTypeRequest(action = {}) {
  const {payload, callback = () => {}} = action;
  try {
    const response = yield call(getTypesAsync, payload);
    if (response.data) {
      const data = response.data.results || [];
      const typesData = data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      callback(typesData);
    } else {
      callback();
    }
  } catch (_e) {
    callback();
  }
}

function* sensorGroupByCategory(action = {}) {
  const {payload, callback = () => {}} = action;
  try {
    const response = yield call(getGroupsByCategoryAsync, payload);
    if (response.data) {
      const data = response.data.results || [];
      const groupsData = data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      callback(groupsData);
    } else {
      callback();
    }
  } catch (_e) {
    callback();
  }
}

function* sensorGroupRequest(action = {}) {
  const {callback = () => {}} = action;

  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(getGroupsAsync, {
      mac_id: selectedController.mac,
    });
    if (response.data) {
      const data = response.data.results || [];
      const groupsData = data.map((item) => ({
        label: item.name,
        value: item.id,
      }));
      callback(groupsData);
    } else {
      callback();
    }
  } catch (_e) {
    /* empty */
  }
}

function* selectedSensorGroupTypeRequest(action = {}) {
  const {payload, callback1 = () => {}, callback2 = () => {}} = action;
  try {
    const response = yield call(getSelectedGroupTypeAsync, {sensorId: payload});
    if (response.data) {
      callback1(response.data.group);
      callback2(response.data.type);
    } else {
      callback1();
      callback2();
    }
  } catch (_e) {
    callback1();
    callback2();
  }
}

function* updateSensorGroupType(action = {}) {
  const {payload, callback = () => {}} = action;
  try {
    const response = yield call(
      updateSensorGroupTypeAsync,
      {sensorId: payload.sensorId},
      {group: payload.selectedGroup, type: payload.selectedType}
    );
    if (response) {
      callback();
      yield call(getSummaryDataRequest, {trackThisPromise: true});
    }
  } catch (e) {
    return e;
  }
}
function* sensorListRequest(action = {}) {
  const {payload = {}, callback = () => {}} = action;
  const {IO_TYPE_PROBE, probe} = payload;
  const selectedController = yield select(getSelectedController);

  try {
    const response = yield call(getSensorListAsync, {
      mac_id: selectedController?.mac,
      IO_TYPE_PROBE,
      probe,
    });
    if (response?.data) {
      callback(response?.data?.data);
    } else {
      callback();
    }
  } catch (_e) {
    callback();
  }
}

function* getAvailablePins(action = {}) {
  const {payload, callback = () => {}} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(getAvailablePinsAsync, {
      mac_id: selectedController?.mac,
      ioType: payload?.IO_TYPE_PROBE,
      probeType: payload?.probe,
      sensorId: payload?.sensor,
    });
    if (response?.data) {
      callback(response?.data?.data);
    } else {
      callback();
    }
  } catch (_e) {
    callback();
  }
}

function* createProbeRequest(action = {}) {
  const {payload, callback = () => {}} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(
      createProbesAsync,
      {
        mac_id: selectedController?.mac,
        ioType: payload?.IO_TYPE_PROBE,
        probeType: payload?.probe,
        sensorId: payload?.sensor,
      },
      {pin: payload?.pin, type_id: payload?.type_id, group_id: payload?.group_id}
    );
    if (response?.data) {
      yield callback();
      yield call(getSummaryDataRequest, {trackThisPromise: true});
    }
  } catch (_e) {
    //
  }
}

function* getSummaryDataRequest(action = {}) {
  const {trackThisPromise} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(getSummaryDataAsync, {
      mac_id: selectedController?.mac,
      trackThisPromise,
    });
    if (response?.data) {
      yield put({
        type: actions.STORE_SYSTEM_SUMMARY_DATA,
        payload: response?.data?.data,
      });
    }
  } catch (_e) {
    //
  }
}

function* getSummarySensorDataRequest(action = {}) {
  const selectedController = yield select(getSelectedController);
  const {payload, trackThisPromise = true} = action;
  try {
    const response = yield call(getSummarySensorDataAsync, {
      mac_id: selectedController?.mac,
      sensor_id: payload,
      trackThisPromise,
    });
    if (response?.data) {
      yield put({
        type: actions.STORE_INVENTORY_SENSOR,
        payload: response?.data?.data,
      });
    }
  } catch (_e) {
    //
  }
}

function* getSensorCalibrateCommandsRequest(action) {
  const selectedController = yield select(getSelectedController);
  const sensorId = action?.payload;
  try {
    const response = yield call(getSensorCalibrationCommandsAsync, {
      mac_id: selectedController?.mac,
      sensor_id: sensorId,
    });
    if (response?.data) {
      yield put({
        type: actions.STORE_INVENTORY_SENSOR_CALIBRATE_CMDS,
        payload: response?.data?.data,
      });
    }
  } catch (_e) {
    //
  }
}

function* runSensorCalibrateCommandsRequest(action = {}) {
  const selectedController = yield select(getSelectedController);
  const {payload = {}} = action;
  const {sensorId = ""} = payload;
  try {
    const response = yield call(
      runSensorCalibrationCommandsAsync,
      {
        mac_id: selectedController?.mac,
        sensor_id: sensorId,
      },
      payload
    );
    if (response?.data) {
      yield put({
        type: actions.REQUEST_INVENTORY_SENSOR_CALIBRATION_HISTORY,
        payload: sensorId,
      });
    }
  } catch (_e) {
    //
  }
}

function* getSensorCalibrationHistoryRequest(action = {}) {
  const selectedController = yield select(getSelectedController);
  const {payload, trackThisPromise = true} = action;
  try {
    const response = yield call(getSensorCalibrationHistoryAsync, {
      mac_id: selectedController?.mac,
      sensor_id: payload,
      trackThisPromise,
    });
    if (response) {
      yield put({
        type: actions.STORE_INVENTORY_SENSOR_CALIBRATION_HISTORY,
        payload: response?.data,
      });
    }
  } catch (_e) {
    //
  }
}

function* getSensorDetailsRequest(action = {}) {
  const {payload, callback = () => {}, routeAction = "Edit"} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(
      getSensorDetailsAsync,
      {
        mac_id: selectedController?.mac,
        sensorId: payload,
      },
      routeAction
    );
    if (callback && response?.data) {
      const formattedData = response?.data?.data?.sensor?.metadata?.map(
        (eachMetaData) => {
          // The Patch
          return {
            ...eachMetaData,
            page_section: orderBy(
              eachMetaData?.page_section?.map((section /*index*/) => ({
                ...section,
                active: section?.order === 0,
              })),
              ["order"],
              ["asc"]
            ),
          };
        }
      );

      callback(
        cloneDeep({
          ...response?.data?.data,
          sensor: {...response?.data?.data?.sensor, metadata: formattedData},
        })
      );
    }
  } catch (e) {
    /* empty */
  }
}

function* updateSensorDetailsRequest(action = {}) {
  const {payload = {}, callback = () => {}} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(
      updateSensorDetailsAsync,
      {
        mac_id: selectedController?.mac,
        sensorId: payload?.id,
      },
      payload
    );
    if (response?.data) {
      yield call(getSummaryDataRequest, {trackThisPromise: true});
      if (callback) {
        callback();
      }
      yield put({type: actions.RESET_DRAFT_SENSOR_DATA});
    }
  } catch (_e) {
    //  Let user fallback on summary page if Edit config fail.
  }
}

function* sendDeleteSensorRequest(action = {}) {
  try {
    const {payload = "", callback = () => {}} = action;
    const selectedController = yield select(getSelectedController);
    const response = yield call(deleteSensorDetailsAsync, {
      mac_id: selectedController?.mac,
      sensorId: payload,
    });
    if (response) {
      callback();
      yield call(getSummaryDataRequest, {trackThisPromise: true});
    }
  } catch (e) {
    return e;
  }
}

function* sendResetLimitSensorRequest(action = {}) {
  try {
    const {payload = {}, callback = () => {}} = action;
    const selectedController = yield select(getSelectedController);
    const response = yield call(resetLimitSensorAsync, {
      mac_id: selectedController?.mac,
      sensorId: payload,
    });
    if (response) {
      callback();
      yield call(getSummaryDataRequest, {trackThisPromise: true});
    }
  } catch (e) {
    return e;
  }
}

function* acknowledgeOnOff(action = {}) {
  try {
    const {payload = ""} = action;
    const selectedController = yield select(getSelectedController);
    const response = yield call(acknowledgePowerOnOff, {
      mac_id: selectedController?.mac,
      sensorId: payload,
    });
    if (response) {
      yield call(getSummaryDataRequest, {trackThisPromise: true});
    }
  } catch (e) {
    return e;
  }
}

function* testFeed(action = {}) {
  const {payload = {}} = action;

  const selectedController = yield select(getSelectedController);
  try {
    yield call(requestTestFeedAsync, {
      mac_id: selectedController?.mac,
      sensorId: payload?.id,
    });
  } catch (_e) {
    //
  }
}

function* addSlugFeed(action = {}) {
  const {payload = {}, callback = () => {}} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(
      requestAddSlugFeedAsync,
      {
        mac_id: selectedController?.mac,
        sensorId: payload?.id,
      },
      {
        run_time: payload?.minutes,
      }
    );
    if (response && callback) {
      callback();
    }
  } catch (_e) {
    //
  }
}

function* getSystemLogsLoad(action = {}) {
  const {pageUrl = "", params = {}} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(
      requestSystemLogsAsync,
      selectedController?.mac,
      pageUrl,
      params
    );
    if (response?.data) {
      yield put({
        type: actions.STORE_SYSTEM_LOGS,
        payload: response?.data,
      });
    }
  } catch (_e) {
    //
  }
}

function* getSensorSummary(action = {}) {
  const selectedController = yield select(getSelectedController);
  const {payload} = action;
  try {
    const response = yield call(requestSensorSummaryAsync, {
      mac_id: selectedController?.mac,
      sensor_id: payload,
    });
    if (response?.data)
      yield put({
        type: actions.STORE_SENSOR_SUMMARY_DATA,
        payload: response?.data?.data,
      });
  } catch (_e) {
    //
  }
}

function* requestAnalogBank() {
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(requestAnalogBankAsync, {
      mac_id: selectedController?.mac,
    });
    if (response?.data)
      yield put({
        type: actions.STORE_ANALOG_BANK,
        payload: response?.data?.data,
      });
  } catch (_e) {
    //
  }
}

function* editControllerRequest(action = {}) {
  const {payload, callback = () => {}} = action;
  try {
    const selectedController = yield select(getSelectedController);
    if (selectedController?.mac) {
      const response = yield call(
        editController,
        {mac_id: selectedController?.mac},
        payload
      );
      if (response?.data && callback) {
        callback();
        yield call(systemPropertiesRequest, {trackPromise: true});
        yield call(getSummaryDataRequest, {trackThisPromise: true});
      }
    }
  } catch (e) {
    /* empty */
  }
}

function* sendAnalogBank(action = {}) {
  const {payload, callback = () => {}} = action;

  try {
    const selectedController = yield select(getSelectedController);
    if (selectedController?.mac) {
      const response = yield call(
        sendAnalogBankAsync,
        {mac_id: selectedController?.mac},
        payload
      );
      if (response?.data && callback) {
        callback();
        yield fork(requestAnalogBank);
      }
    }
  } catch (e) {
    /* empty */
  }
}

function* requestFavourites() {
  try {
    const response = yield call(requestFavouritesAsync);
    if (response?.data) {
      yield put({
        type: actions.STORE_SYSTEM_FAVOURITES,
        payload: response?.data?.data,
      });
    }
  } catch (_e) {
    return _e;
  }
}

function* requestFavourite() {
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(requestFavouriteAsync, {
      mac_id: selectedController?.mac,
    });
    if (response?.data) {
      yield put({
        type: actions.STORE_SYSTEM_FAVOURITE,
        payload: response?.data?.data,
      });
    }
  } catch (_e) {
    //
  }
}

function* sendFavourite(action) {
  const {payload} = action;
  try {
    const selectedController = yield select(getSelectedController);
    if (selectedController?.mac) {
      const response = yield call(
        sendFavouriteAsync,
        {mac_id: selectedController?.mac},
        payload
      );
      if (response?.data) {
        yield fork(requestFavourite);
      }
    }
  } catch (e) {
    /* empty */
  }
}

function* requestSnapshot(action = {}) {
  const {pageUrl = ""} = action;
  const selectedController = yield select(getSelectedController);
  try {
    const response = yield call(requestSnapshotAsync, {
      mac_id: selectedController?.mac,
      pageUrl,
    });
    if (response?.data) {
      yield put(storeSnapshot(response.data));
    }
  } catch (_e) {
    //
  }
}

function* requestResetController(action) {
  const {callback = () => {}} = action;

  try {
    const selectedController = yield select(getSelectedController);
    if (selectedController?.mac) {
      const response = yield call(resetControllerAsync, selectedController?.mac);
      if (response?.data) {
        callback();
        yield call(systemPropertiesRequest, {trackPromise: true});
      }
    }
  } catch (e) {
    return e;
  }
}

// Saga to handle fetching categories
function* categoriesRequest() {
  try {
    const response = yield call(getCategories);
    yield put({
      type: actions.STORE_CATEGORIES,
      payload: response.data,
    });
  } catch (e) {
    /* empty */
  }
}

function* getControllersWithActiveAlert(action = {}) {
  const {pageUrl = ""} = action;
  try {
    const response = yield call(getControllersWithActiveAlertAsync, pageUrl);
    if (response) {
      yield put({
        type: actions.SAVE_CONTROLLERS_WITH_ACTIVE_ALERT,
        payload: response?.data?.data || [],
      });
    }
  } catch (_e) {
    return _e;
  }
}
function* controllerSensorDataRequest(action = {}) {
  const {callback = () => {}} = action;

  try {
    // If the modal closes mid-request, drop the response instead of storing it.
    const {response, cancelled} = yield race({
      response: call(getControllerSensorDataAsync),
      cancelled: take(actions.RESET_CONTROLLER_SENSOR_DATA),
    });

    if (cancelled) {
      return;
    }

    if (response?.data) {
      yield put({
        type: actions.STORE_CONTROLLER_SENSOR_DATA,
        payload: response.data,
      });

      callback(response.data);
    }
  } catch (_e) {
    return _e;
  }
}

export function* controllersSaga() {
  yield takeLatest(actions.REQUEST_CONTROLLERS_SAGA, controllersRequest);
  yield takeLatest(actions.REQUEST_TELEMETRY, telemetryRequest);
  yield takeLatest(actions.REQUEST_SYSTEM_PROPERTIES_SAGA, systemPropertiesRequest);
  yield takeLatest(actions.REQUEST_IO_TYPES_SAGA, ioTypesRequest);
  yield takeLatest(actions.REQUEST_PROBES_TYPES_SAGA, probesRequest);
  yield takeLatest(actions.REQUEST_SENSOR_LIST_SAGA, sensorListRequest);
  yield takeLatest(actions.REQUEST_PINS_LIST_SAGA, getAvailablePins);
  yield takeLatest(actions.REQUEST_CREATE_PROBE_SAGA, createProbeRequest);
  yield takeLatest(actions.REQUEST_SYSTEM_SUMMARY_DATA_SAGA, getSummaryDataRequest);
  yield takeLatest(actions.REQUEST_INVENTORY_SENSOR, getSummarySensorDataRequest);
  yield takeLatest(actions.REQUEST_SENSOR_DETAILS_SAGA, getSensorDetailsRequest);
  yield takeLatest(
    actions.REQUEST_UPDATE_SENSOR_DETAILS_SAGA,
    updateSensorDetailsRequest
  );
  yield takeLatest(actions.REQUEST_DELETE_SENSOR_SAGA, sendDeleteSensorRequest);
  yield takeLatest(actions.REQUEST_RESET_LIMIT_SENSOR, sendResetLimitSensorRequest);
  yield takeLatest(actions.REQUEST_ACKNOWLEDGE_POWER, acknowledgeOnOff);
  yield takeLatest(actions.TEST_FEED_SAGA, testFeed);
  yield takeLatest(actions.ADD_SLUG_FEED_SAGA, addSlugFeed);
  yield takeLatest(actions.REQUEST_SYSTEM_LOGS_SAGA, getSystemLogsLoad);
  yield takeLatest(actions.REQUEST_SENSOR_SUMMARY_DATA, getSensorSummary);
  yield takeLatest(actions.SEND_EDIT_CONTROLLER_DATA_SAGA, editControllerRequest);
  yield takeLatest(actions.REQUEST_ANALOG_BANK, requestAnalogBank);
  yield takeLatest(actions.SEND_ANALOG_BANK, sendAnalogBank);
  yield takeLatest(actions.REQUEST_SYSTEM_FAVOURITES, requestFavourites);
  yield takeLatest(actions.REQUEST_SYSTEM_FAVOURITE, requestFavourite);
  yield takeLatest(actions.SEND_SYSTEM_FAVOURITE, sendFavourite);
  yield takeLatest(actions.REQUEST_SNAPSHOT, requestSnapshot);
  yield takeLatest(
    actions.REQUEST_INVENTORY_SENSOR_CALIBRATE_CMDS,
    getSensorCalibrateCommandsRequest
  );
  yield takeLatest(actions.RUN_CALIBRATION, runSensorCalibrateCommandsRequest);
  yield takeLatest(
    actions.REQUEST_INVENTORY_SENSOR_CALIBRATION_HISTORY,
    getSensorCalibrationHistoryRequest
  );
  yield takeLatest(actions.RESET_CONTROLLER, requestResetController);
  yield takeLatest(actions.GET_CATEGORIES, categoriesRequest);
  yield takeLatest(actions.REQUEST_SENSORGROUPS_BYCATEGORY, sensorGroupByCategory);
  yield takeLatest(actions.REQUEST_SENSORTYPES_SAGA, sensorTypeRequest);
  yield takeLatest(actions.REQUEST_SENSORGROUPS_SAGA, sensorGroupRequest);
  yield takeLatest(
    actions.REQUEST_SENSORGROUPTYPE_SELECTED_SAGA,
    selectedSensorGroupTypeRequest
  );
  yield takeLatest(actions.UPDATE_SENSORGROUPTYPE, updateSensorGroupType);
  yield takeLatest(
    actions.REQUEST_CONTROLLERS_WITH_ACTIVE_ALERT,
    getControllersWithActiveAlert
  );
  yield takeLatest(actions.REQUEST_CONTROLLER_SENSOR_DATA, controllerSensorDataRequest);
}
